import { useState, useRef, useCallback } from 'react';
import Papa from 'papaparse';
import { booksApi } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, CheckCircle, AlertCircle, BookOpen, Loader2, ExternalLink } from 'lucide-react';

// ─── CSV parser ───────────────────────────────────────────────────────────────

function parseGoodreadsCSV(file) {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^a-z0-9_]/g, ''),
      complete: (results) => {
        const rows = (results.data || [])
          .filter((row) => row.title || row.title_)
          .map((row) => ({
            title: row.title || row.title_ || '',
            author: row.author || row.author_lf || '',
            isbn: row.isbn || '',
            isbn13: row.isbn13 || '',
            myRating: parseInt(row.my_rating, 10) || 0,
            myReview: row.my_review || '',
            exclusiveShelf: row.exclusive_shelf || 'to-read',
            dateRead: row.date_read || '',
          }))
          .filter((r) => r.title);
        resolve(rows);
      },
      error: reject,
    });
  });
}

// ─── Steps ────────────────────────────────────────────────────────────────────

function StepInstructions({ onFileSelected, dragging, setDragging }) {
  const inputRef = useRef(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file?.name.endsWith('.csv')) onFileSelected(file);
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-5">
      {/* How-to steps */}
      <ol className="space-y-3">
        {[
          { n: 1, text: 'Go to goodreads.com and sign in' },
          { n: 2, text: 'Click My Books → Import and Export' },
          { n: 3, text: 'Click "Export Library" and download the CSV file' },
          { n: 4, text: 'Upload the file below' },
        ].map(({ n, text }) => (
          <li key={n} className="flex items-start gap-3">
            <span className="w-6 h-6 rounded-full bg-terra/10 text-terra text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
              {n}
            </span>
            <span className="text-sm text-ink-light">{text}</span>
          </li>
        ))}
      </ol>

      <a
        href="https://www.goodreads.com/review/import"
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-terra hover:text-terra/80 transition-colors"
      >
        Open Goodreads Export page <ExternalLink className="w-3 h-3" />
      </a>

      <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        <p className="text-xs text-amber-700">
          <span className="font-medium">Tip:</span> If the "Export Library" button isn't visible on Goodreads, try adding any book to your shelf first, then go back to the export page.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragging ? 'border-terra bg-terra/5' : 'border-cream-dark hover:border-terra/50'
        }`}
      >
        <Upload className={`w-8 h-8 mx-auto mb-3 ${dragging ? 'text-terra' : 'text-ink-muted'}`} />
        <p className="text-sm font-medium text-ink mb-1">
          {dragging ? 'Drop it here!' : 'Drop your CSV here'}
        </p>
        <p className="text-xs text-ink-muted">or click to browse</p>
        <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}

function StepImporting({ progress }) {
  const pct = progress.total > 0 ? Math.round((progress.current / progress.total) * 100) : 0;

  return (
    <div className="space-y-5 py-2">
      <div className="flex items-center gap-3">
        <Loader2 className="w-5 h-5 text-terra animate-spin flex-shrink-0" />
        <p className="text-sm font-medium text-ink">Importing your library…</p>
      </div>

      <div className="space-y-1.5">
        <div className="flex justify-between text-xs text-ink-muted">
          <span>{progress.current} / {progress.total} books</span>
          <span>{pct}%</span>
        </div>
        <div className="w-full bg-cream-dark rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-terra rounded-full transition-all duration-200"
            style={{ width: `${pct}%` }}
          />
        </div>
        {progress.title && (
          <p className="text-xs text-ink-muted truncate">{progress.title}</p>
        )}
      </div>

      <p className="text-xs text-ink-muted">
        This may take a minute for large libraries. Please keep this window open.
      </p>
    </div>
  );
}

function StepDone({ results, onClose, onViewShelf }) {
  const total = results.imported + results.updated + results.skipped;
  const hasChanges = results.imported > 0 || results.updated > 0;
  
  return (
    <div className="space-y-5 py-2 text-center">
      <div className="flex flex-col items-center gap-2">
        <CheckCircle className="w-12 h-12 text-forest" />
        <h3 className="font-heading text-xl text-ink">Import complete!</h3>
        <p className="text-sm text-ink-muted">
          {hasChanges ? (
            <>
              {results.imported > 0 && `Added ${results.imported} new book${results.imported !== 1 ? 's' : ''}`}
              {results.imported > 0 && results.updated > 0 && ' and '}
              {results.updated > 0 && `updated ${results.updated} existing book${results.updated !== 1 ? 's' : ''} with read dates`}
              .
            </>
          ) : (
            "No new books to add — your shelf is already up to date."
          )}
        </p>
      </div>

      {results.imported > 0 && (
        <div className="grid grid-cols-3 gap-3 text-center">
          {[
            { label: 'Read', value: results.byShelf.read, color: 'text-forest' },
            { label: 'Reading', value: results.byShelf.reading, color: 'text-terra' },
            { label: 'Want', value: results.byShelf.want, color: 'text-ink-light' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-cream-dark rounded-xl p-3">
              <p className={`text-2xl font-heading ${color}`}>{value}</p>
              <p className="text-xs text-ink-muted mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {results.failed > 0 && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-left">
          <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            {results.failed} book{results.failed !== 1 ? 's' : ''} couldn't be imported. You can add them manually later.
          </p>
        </div>
      )}

      <div className="flex gap-3 justify-center pt-1">
        <Button variant="outline" onClick={onClose} className="border-cream-dark text-ink-muted">
          Close
        </Button>
        <Button onClick={onViewShelf} className="bg-terra hover:bg-terra/90 text-cream">
          <BookOpen className="w-4 h-4 mr-1.5" />
          View my shelf
        </Button>
      </div>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function GoodreadsImportModal({ open, onClose, onImportDone }) {
  const [step, setStep] = useState('instructions'); // instructions | importing | done
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, title: '' });
  const [results, setResults] = useState(null);

  const handleClose = () => {
    if (step === 'importing') return; // don't close mid-import
    setStep('instructions');
    setProgress({ current: 0, total: 0, title: '' });
    setResults(null);
    onClose();
  };

  const handleFileSelected = useCallback(async (file) => {
    try {
      const rows = await parseGoodreadsCSV(file);
      if (rows.length === 0) return;
      setStep('importing');
      setProgress({ current: 0, total: rows.length, title: '' });
      const result = await booksApi.importGoodreads(rows, (p) => setProgress(p));
      setResults(result);
      setStep('done');
      onImportDone?.();
    } catch (_) {
      setStep('instructions');
    }
  }, [onImportDone]);

  const handleViewShelf = () => {
    handleClose();
    // Navigation handled by parent
    onImportDone?.('shelf');
  };

  return (
    <Dialog open={open} onOpenChange={step !== 'importing' ? handleClose : undefined}>
      <DialogContent className="bg-cream border-cream-dark max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-ink flex items-center gap-2">
            <span className="text-lg">📚</span>
            {step === 'done' ? 'Import complete' : 'Import from Goodreads'}
          </DialogTitle>
        </DialogHeader>

        {step === 'instructions' && (
          <StepInstructions
            onFileSelected={handleFileSelected}
            dragging={dragging}
            setDragging={setDragging}
          />
        )}
        {step === 'importing' && <StepImporting progress={progress} />}
        {step === 'done' && results && (
          <StepDone results={results} onClose={handleClose} onViewShelf={handleViewShelf} />
        )}
      </DialogContent>
    </Dialog>
  );
}
